/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  createEmptyStateResource,
  EMPTY_STRING,
  isStateResourceStable,
  ResourceService,
  ResourceServiceConfig,
  StateResource,
  StateService,
} from '@common/tech-shared';
import { inject, Injectable } from '@angular/core';
import { Resource } from '@ngxp/rest';
import { filter, first, Observable, startWith, switchMap } from 'rxjs';
import { CommandResource, CreateCommand, CreateCommandActionProps, DeleteCommandProps, SaveCommandProps } from './command.model';
import { tapOnCommandSuccessfullyDone } from './command.rxjs.operator';
import { CommandService } from './command.service';
import { getEffectedResourceUrl } from './command.util';

@Injectable()
export class CommandResourceService<B extends Resource, T extends Resource> extends ResourceService<B, T> {
  private readonly commandService: CommandService = inject(CommandService);

  constructor(
    protected config: ResourceServiceConfig<B>,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    protected oldStateService?: StateService,
    /**
     * @deprecated Der Parameter wird nicht mehr gebraucht.
     */
    private readonly oldCommandService?: CommandService,
  ) {
    super(config);
  }

  public save(
    toSave: unknown,
    saveCommandProps: SaveCommandProps = { snackBarMessage: EMPTY_STRING }, //NOSONAR
  ): Observable<StateResource<CommandResource>> {
    return this.selectResource().pipe(
      first(),
      switchMap((stateResource: StateResource<T>) => this.doSave(stateResource.resource, toSave, saveCommandProps)),
      tapOnCommandSuccessfullyDone((commandStateResource: StateResource<CommandResource>) =>
        this.loadByResourceUri(getEffectedResourceUrl(commandStateResource.resource)),
      ),
      filter(isStateResourceStable),
      startWith(createEmptyStateResource<CommandResource>(true)),
    );
  }

  doSave(resource: T, toSave: unknown, saveCommandProps: SaveCommandProps): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand(this.buildSaveCommandProps<T>(resource, toSave, saveCommandProps));
  }

  buildSaveCommandProps<T extends Resource>(
    resource: T,
    toSave: unknown,
    saveCommandProps: SaveCommandProps,
  ): CreateCommandActionProps {
    return {
      resource,
      linkRel: this.config.edit.linkRel,
      command: {
        order: this.config.edit.order,
        body: toSave,
      },
      snackBarMessage: saveCommandProps.snackBarMessage,
    };
  }

  public delete(
    deleteCommandProps: DeleteCommandProps = { snackBarMessage: EMPTY_STRING }, //NOSONAR
  ): Observable<StateResource<CommandResource>> {
    return this.selectResource().pipe(
      first(),
      switchMap((stateResource: StateResource<T>) => this.doDelete(stateResource.resource, deleteCommandProps)),
      tapOnCommandSuccessfullyDone(() => this.clearResource()),
      filter(isStateResourceStable),
      startWith(createEmptyStateResource<CommandResource>(true)),
    );
  }

  doDelete(resource: T, deleteCommandProps: DeleteCommandProps): Observable<StateResource<CommandResource>> {
    return this.commandService.createCommand(this.buildDeleteCommandProps(resource, deleteCommandProps));
  }

  buildDeleteCommandProps<T extends Resource>(resource: T, deleteCommandProps: DeleteCommandProps): CreateCommandActionProps {
    return {
      resource,
      linkRel: this.config.delete.linkRel,
      command: {
        order: this.config.delete.order,
        body: null,
      },
      snackBarMessage: deleteCommandProps.snackBarMessage,
    };
  }

  public createCommand(
    linkRel: string,
    command: CreateCommand,
    snackBarMessage: (resource: T) => string = () => EMPTY_STRING,
  ): Observable<StateResource<CommandResource>> {
    return this.selectResource().pipe(
      first(),
      switchMap((stateResource: StateResource<T>) =>
        this.commandService.createCommand({
          resource: stateResource.resource,
          linkRel,
          command,
          snackBarMessage: snackBarMessage(stateResource.resource),
        }),
      ),
    );
  }
}
